/**
 * The one and only HTTP client.
 *
 * ── Swapping the mock API for Spring Boot ─────────────────────────────────────
 * Nothing in this file needs to change. Set in `.env`:
 *     VITE_USE_MOCKS=false
 *     VITE_API_BASE_URL=http://localhost:8080/api/v1   (or /api/v1 + Vite proxy)
 * The interceptors below already speak the conventions Spring Boot uses:
 *   - `Authorization: Bearer <jwt>` on every request
 *   - `ApiResponse<T>` unwrapping (toggle with VITE_API_ENVELOPE)
 *   - Spring `Pageable` params (page/size/sort=field,dir)
 *   - `ProblemDetail`/custom error bodies normalised into `ApiError`
 */

import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { ApiError, type ApiErrorBody, type ApiResponse, type PageRequest } from '@/types/common';
import { env, storageKeys } from './env';

export const http: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

/* -------------------------------------------------------------------------- */
/* Token handling                                                             */
/* -------------------------------------------------------------------------- */

export const tokenStore = {
  get: () => localStorage.getItem(storageKeys.accessToken),
  set: (token: string) => localStorage.setItem(storageKeys.accessToken, token),
  clear: () => {
    localStorage.removeItem(storageKeys.accessToken);
    localStorage.removeItem(storageKeys.refreshToken);
  },
};

/** Called by the auth store when a 401 comes back, so routing stays out of here. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

http.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* -------------------------------------------------------------------------- */
/* Response / error normalisation                                             */
/* -------------------------------------------------------------------------- */

http.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorBody>) => {
    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        tokenStore.clear();
        onUnauthorized?.();
      }

      return Promise.reject(
        new ApiError(
          data?.message || defaultMessageFor(status),
          status,
          data?.errorCode,
          data?.fieldErrors,
        ),
      );
    }

    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new ApiError('The request timed out. Please try again.', 0, 'TIMEOUT'));
    }

    return Promise.reject(
      new ApiError(
        'Cannot reach the server. Check your connection and try again.',
        0,
        'NETWORK_ERROR',
      ),
    );
  },
);

function defaultMessageFor(status: number): string {
  switch (status) {
    case 400:
      return 'The request was invalid.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with the current state of the record.';
    case 422:
      return 'Some of the submitted values are invalid.';
    default:
      return status >= 500 ? 'Something went wrong on the server.' : 'The request failed.';
  }
}

/* -------------------------------------------------------------------------- */
/* Verb helpers — every api/*.api.ts file goes through these                   */
/* -------------------------------------------------------------------------- */

/** Unwraps `ApiResponse<T>` when the backend uses the envelope. */
function unwrap<T>(payload: ApiResponse<T> | T): T {
  if (
    env.apiEnvelope &&
    payload !== null &&
    typeof payload === 'object' &&
    'data' in (payload as Record<string, unknown>) &&
    'success' in (payload as Record<string, unknown>)
  ) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
}

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.get<ApiResponse<T> | T>(url, config);
  return unwrap<T>(data);
}

export async function post<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await http.post<ApiResponse<T> | T>(url, body, config);
  return unwrap<T>(data);
}

export async function put<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await http.put<ApiResponse<T> | T>(url, body, config);
  return unwrap<T>(data);
}

export async function patch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await http.patch<ApiResponse<T> | T>(url, body, config);
  return unwrap<T>(data);
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.delete<ApiResponse<T> | T>(url, config);
  return unwrap<T>(data);
}

/** Downloads a file (report export, label PDF) as a Blob. */
export async function getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
  const { data } = await http.get<Blob>(url, { ...config, responseType: 'blob' });
  return data;
}

/* -------------------------------------------------------------------------- */
/* Query param helpers                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Serialises our `PageRequest` into what Spring's `Pageable` resolver expects
 * (`?page=0&size=10&sort=createdAt,desc`) and drops empty values so the URL
 * stays clean and query keys stay stable.
 */
export function toQueryParams<T extends object>(
  input: T = {} as T,
): Record<string, string | number | boolean> {
  const { page, size, sortBy, sortDir, ...rest } = input as PageRequest & Record<string, unknown>;
  const params: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined || value === null || value === '' || value === 'ALL') continue;
    params[key] = value as string | number | boolean;
  }
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  if (sortBy) params.sort = `${sortBy},${sortDir ?? 'desc'}`;

  return params;
}
